import type { Observable } from 'rxjs';
import type { Update } from '@ngrx/entity';
import type { TabEntity, GridEntity } from '@sites/entity-core';
import type {
    NormalizedBlockEntity,
    NormalizedSpecificSectionEntity,
    NormalizedColumnEntity,
    WebLegacyWidgetEntity,
} from '@web/types';
import type { BatchEntry, BatchHistoryChange, HistoryChange } from './history.model';
import { HistoryChangeAction } from './history.model';
import type { HistoryState } from './history.reducer';

import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import * as HistorySelectors from './history.selectors';
import HistoryActions from './history.actions';

@Injectable()
export class HistoryFacade {
    private readonly store = inject<Store<HistoryState>>(Store);

    public readonly prev$: Observable<HistoryChange[]> = this.store.select(HistorySelectors.selectHistoryPrev);
    public readonly isPrevEmpty$: Observable<boolean> = this.store.select(HistorySelectors.selectIsHistoryPrevEmpty);
    public readonly lastPrev$: Observable<HistoryChange> = this.store.select(HistorySelectors.selectHistoryLastPrev);
    public readonly next$: Observable<HistoryChange[]> = this.store.select(HistorySelectors.selectHistoryNext);
    public readonly isNextEmpty$: Observable<boolean> = this.store.select(HistorySelectors.selectIsHistoryNextEmpty);
    public readonly lastNext$: Observable<HistoryChange> = this.store.select(HistorySelectors.selectHistoryLastNext);

    public undo(): void {
        this.store.dispatch(HistoryActions.undoRequest());
    }

    public redo(): void {
        this.store.dispatch(HistoryActions.redoRequest());
    }

    public addBlockUpdateChangeToHistory(
        before: Update<NormalizedBlockEntity>,
        after: Update<NormalizedBlockEntity>
    ): void {
        this.store.dispatch(HistoryActions.addBlockUpdateChangeToHistory({ before, after }));
    }

    public addColumnsUpdateChangeToHistory(
        before: Update<NormalizedColumnEntity>[],
        after: Update<NormalizedColumnEntity>[]
    ): void {
        this.store.dispatch(HistoryActions.addColumnsUpdateChangeToHistory({ before, after }));
    }

    public addGridsUpdateChangeToHistory(before: Update<GridEntity>[], after: Update<GridEntity>[]): void {
        this.store.dispatch(HistoryActions.addGridsUpdateChangeToHistory({ before, after }));
    }

    public addGridUpdateChangeToHistory(before: Update<GridEntity>, after: Update<GridEntity>): void {
        this.store.dispatch(HistoryActions.addGridUpdateChangeToHistory({ before, after }));
    }

    public addTabUpdateChangeToHistory(before: Update<TabEntity>, after: Update<TabEntity>): void {
        this.store.dispatch(HistoryActions.addTabUpdateChangeToHistory({ before, after }));
    }

    public addWidgetUpdateChangeToHistory(
        before: Update<WebLegacyWidgetEntity>,
        after: Update<WebLegacyWidgetEntity>
    ): void {
        this.store.dispatch(HistoryActions.addWidgetUpdateChangeToHistory({ before, after }));
    }

    public addStoreUpdateChangeToHistory(before: Update<any>, after: Update<any>): void {
        this.store.dispatch(HistoryActions.addStoreUpdateChangeToHistory({ before, after }));
    }

    public addBatchEntry(entry: BatchEntry): void {
        this.store.dispatch(HistoryActions.addBatchChangeToHistory({ entry }));
    }

    public addBatchUpdateToHistory(entityType: string, before: Update<any>, after: Update<any>): void {
        this.store.dispatch(
            HistoryActions.addBatchChangeToHistory({
                entry: { action: HistoryChangeAction.update, entityType, before, after },
            })
        );
    }

    /**
     * One undo step for a whole AI reply, dispatched already assembled — unlike `addBatchEntry`,
     * this skips `HistoryEffectsV2`'s 500ms debounce-collector entirely, because the caller
     * (`McpToolsService.flushAiHistoryBatch`) has already collected every entity this reply touched
     * and knows the reply is over. Tagged `origin: 'ai'` for the reducer's separate depth cap.
     */
    public addAiBatchToHistory(entries: BatchEntry[]): void {
        if (!entries.length) {
            return;
        }

        const change: BatchHistoryChange = {
            action: HistoryChangeAction.batch,
            version: 2,
            payload: { entries },
            origin: 'ai',
        };

        this.store.dispatch(HistoryActions.addChangeToHistory({ change }));
    }
}
